<template>
  <ViewWrapper :scroll-steps="scrollSteps" class="global_shock" footer-orientation="vertical">
    <ScrollFade
      #default="{ id }"
      :scroll-position="scrollPosition"
      :min-max-map="{
        introduction: [{ max: showCtaValue }],
        cta: [{ min: showCtaValue }],
      }"
    >
      <div
        v-if="id === 'introduction'"
        class="global_shock__intro"
        v-on="voiceOverPlayed ? { click: playVoiceOver } : {}"
      >
        <h2 class="global_shock__headline">{{ $t('view.globalShock.intro.headline') }}</h2>
        <p>{{ $t('view.globalShock.intro.copy') }}</p>
      </div>
      <h2
        v-else-if="id === 'cta'"
        class="global_shock__cta"
        v-on="voiceOverPlayed ? { click: playCta } : {}"
      >
        {{ $t('view.globalShock.cta.headline') }}
      </h2>
    </ScrollFade>

    <template #footer>
      <transition name="fade" mode="out-in">
        <InteractionHint
          v-if="scrollPosition <= showCtaValue"
          class="global_shock__scrollhint"
          :label="$t('ui.interactionHint.scroll')"
          icon="arrows-down"
          animation-class="moveUpDown"
          :interaction-timeout="2000"
          :interaction-trigger="scrollPosition"
        />

        <ButtonPill
          v-else
          class="global_shock__continue_button"
          :to="{ name: 'globalCountdown' }"
          replace
        >
          {{ $t('view.globalShock.cta.buttonLabel') }}
        </ButtonPill>
      </transition>
    </template>
  </ViewWrapper>
</template>

<script>
import { gsap, ScrollToPlugin } from 'gsap/all';
import store from '../store';

import { playSound } from '../services/sound';

import ButtonPill from '../components/ButtonPill';
import InteractionHint from '../components/InteractionHint';
import ScrollFade from '../components/ScrollFade';
import ViewWrapper from '../components/ViewWrapper';

gsap.registerPlugin(ScrollToPlugin);

const SCROLL_STEPS = 2;
const SHOW_CTA_VALUE = 0.7;

export default {
  name: 'GlobalShock',

  components: {
    ButtonPill,
    InteractionHint,
    ScrollFade,
    ViewWrapper,
  },

  props: {
    scrollPosition: {
      type: Number,
      default: undefined,
    },
  },

  data() {
    return {
      scrollSteps: SCROLL_STEPS,
      showCtaValue: SHOW_CTA_VALUE,
      voiceOverPlayed: false,
    };
  },

  watch: {
    scrollPosition: {
      immediate: true,
      handler(scrollPosition) {
        // TODO: the shockwave is not triggered right now
        // it works if the following line is set to
        // store.setGlobeTransitionValue(scrollPosition * 2);
        store.setGlobeTransitionValue(scrollPosition);

        if (scrollPosition >= this.showCtaValue) {
          if (this.voiceOverPlayed) return;
          playSound('voice', 'voiceOver_02', { continue: true }).then(() => {
            this.voiceOverPlayed = true;
          });
        }

        if (scrollPosition >= 1) playSound('fxA', 'shock');
      },
    },
  },

  async mounted() {
    playSound('fxA', 'answer');
    this.playVoiceOver();
  },

  methods: {
    async playVoiceOver() {
      this.voiceOverPlayed = false;
      await playSound('voice', 'voiceOver_01', { continue: true });

      const scrollVal = document.body.scrollHeight * this.showCtaValue;
      gsap.to(window, {
        duration: 2.5,
        scrollTo: { y: scrollVal },
      });
    },

    playCta() {
      playSound('voice', 'voiceOver_02').then(() => {
        this.voiceOverPlayed = true;
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.global_shock {
  position: relative;

  p {
    line-height: 26px;
    max-width: 360px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    text-shadow: 0px 0px 5px $color-black;
  }
}

.global_shock__intro {
  padding-left: 20px;
  padding-right: 20px;
}

.global_shock__headline {
  @include font-headline;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
}

.global_shock__cta {
  @include font-headline;
  text-align: center;
}

.global_shock__scrollhint {
  margin-bottom: calc(20px + env(safe-area-inset-bottom));
}

.global_shock__continue_button {
  margin-bottom: calc(30px + env(safe-area-inset-bottom));
}
</style>
